/**
 * CheckoutAbandonedModal
 *
 * Shown when a user returns from the checkout domain without completing
 * their order (no `te_clear_cart` cookie). Listens for the
 * `checkout:abandoned` custom event dispatched by CheckoutReturnListener.
 *
 * Dismissed state is stored in sessionStorage so the modal only shows
 * once per session (avoids nagging on repeated back-nav).
 */

import styled from '@emotion/styled';
import { useState, useEffect, useCallback } from 'react';
import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  Text,
} from '@template-project/afrodita-components';
import { useCartContext } from '@/lib/context/cart-context';
import { useTranslation } from '@/lib/i18n';
import { env } from '@/lib/utils/env';
import {
  CHECKOUT_ABANDONED_CTA_CONTINUE,
  CHECKOUT_ABANDONED_CTA_LATER,
  CHECKOUT_ABANDONED_DESCRIPTION,
  CHECKOUT_ABANDONED_TITLE,
} from './i18.keys';

const DISMISSED_KEY = 'te_checkout_abandoned_dismissed';

const CartIcon = styled.div`
  width: 56px;
  height: 56px;
  margin: 0 auto 20px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.background.secondary};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
`;

const Title = styled(Text)`
  margin-bottom: 8px;
`;

const Description = styled(Text)`
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: 24px;
  line-height: 1.5;
`;

export function CheckoutAbandonedModal() {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const { cartToken } = useCartContext();

  const dismiss = useCallback(() => {
    sessionStorage.setItem(DISMISSED_KEY, '1');
    setVisible(false);
  }, []);

  useEffect(() => {
    const handler = () => {
      if (sessionStorage.getItem(DISMISSED_KEY) === '1') return;
      if (!cartToken) return;
      setVisible(true);
    };

    window.addEventListener('checkout:abandoned', handler);
    return () => window.removeEventListener('checkout:abandoned', handler);
  }, [cartToken]);

  const isOpen = visible && !!cartToken;
  const checkoutUrl = cartToken ? `${env.CHECKOUT_URL}/checkout/${cartToken}` : '';

  return (
    <Modal variant="centered" size="sm" isOpen={isOpen} onClose={dismiss}>
      <ModalBody>
        <CartIcon>🛒</CartIcon>
        <Title variant="heading4" as="h2">
          {t(CHECKOUT_ABANDONED_TITLE)}
        </Title>
        <Description variant="body">
          {t(CHECKOUT_ABANDONED_DESCRIPTION)}
        </Description>
      </ModalBody>
      <ModalFooter>
        <Button
          as="a"
          href={checkoutUrl}
          variant="secondary"
          fullWidth
          css={{
            padding: 14,
            fontSize: 15,
            fontWeight: 600,
            textDecoration: 'none',
            textAlign: 'center',
          } as any}
        >
          {t(CHECKOUT_ABANDONED_CTA_CONTINUE)}
        </Button>
        <Button
          variant="ghost"
          underline
          fullWidth
          onClick={dismiss}
          css={(theme: any) => ({
            padding: 10,
            fontSize: 14,
            color: theme.colors.text.secondary,
          })}
        >
          {t(CHECKOUT_ABANDONED_CTA_LATER)}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
